/**
 * API client for Bolao 2026
 * Provides typed fetch wrapper with error handling
 */

import type {
  ApiResponse,
  DashboardMetrics,
  SystemHealth,
  AuditLogView,
  Pool,
  Ranking,
  Match,
  PaginatedResponse,
} from '../../shared/types';

type JsonParseResult =
  | { kind: 'empty' }
  | { kind: 'json'; value: unknown }
  | { kind: 'text'; value: string }
  | { kind: 'invalid-json'; rawText: string; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function clipTextForError(value: string, maxLen = 2000): string {
  if (value.length <= maxLen) return value;
  return `${value.slice(0, maxLen)}…`;
}

function detectApiBaseFromPathname(pathname: string): string {
  // Multi-domain support:
  // - conhecendotudo.online/bolao2026/* → API: /bolao2026/api
  // - bolao2026.net/* → API: /api
  return pathname.startsWith('/bolao2026') ? '/bolao2026/api' : '/api';
}

export function resolveApiBase(pathname: string, configuredApiBase?: string | null): string {
  const trimmed = configuredApiBase?.trim();
  if (trimmed) return trimmed.replace(/\/+$/, '');
  return detectApiBaseFromPathname(pathname);
}

function getConfiguredApiBase(): string | null {
  const raw = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL;
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\/+$/, '');
}

function getCurrentPathname(): string {
  // Keep this module import-safe in tests and any future SSR-ish contexts.
  if (typeof window !== 'undefined' && window.location) return window.location.pathname;
  return '/';
}

function getApiBase(): string {
  const configured = getConfiguredApiBase();
  return resolveApiBase(getCurrentPathname(), configured);
}

function parseBody(text: string, contentType: string): JsonParseResult {
  const trimmed = text.trim();
  if (!trimmed) return { kind: 'empty' };

  const lowerType = contentType.toLowerCase();
  const shouldTryJson =
    lowerType.includes('application/json') ||
    trimmed.startsWith('{') ||
    trimmed.startsWith('[');

  if (!shouldTryJson) return { kind: 'text', value: text };

  try {
    return { kind: 'json', value: JSON.parse(text) as unknown };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown JSON parse error';
    return { kind: 'invalid-json', rawText: text, error: message };
  }
}

function extractApiErrorMessage(value: unknown): string | null {
  if (!isRecord(value)) return null;
  const error = value.error;
  if (typeof error === 'string' && error.trim().length > 0) return error;
  return null;
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Fetch wrapper with automatic JSON parsing and error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${getApiBase()}${endpoint}`;

  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  const bodyIsString = typeof options.body === 'string';
  if (bodyIsString && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  const config: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // Include cookies for auth
  };

  try {
    const response = await fetch(url, config);

    // Parse from text so we can handle empty bodies and non-JSON responses deterministically.
    const text = await response.text();
    const body = parseBody(text, response.headers.get('Content-Type') ?? '');

    if (!response.ok) {
      const data =
        body.kind === 'json'
          ? body.value
          : body.kind === 'empty'
              ? {}
              : {
                  rawText: clipTextForError(body.kind === 'text' ? body.value : body.rawText),
                  ...(body.kind === 'invalid-json' ? { parseError: body.error } : {}),
                };

      throw new ApiError(extractApiErrorMessage(data) ?? `HTTP error ${response.status}`, response.status, data);
    }

    if (body.kind === 'empty') return {} as T;
    if (body.kind === 'json') return body.value as T;

    throw new ApiError('Resposta invalida do servidor.', response.status, {
      rawText: clipTextForError(body.kind === 'text' ? body.value : body.rawText),
      ...(body.kind === 'invalid-json' ? { parseError: body.error } : {}),
    });
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or parsing error
    throw new ApiError(
      'Erro de conexão. Verifique sua internet.',
      0,
      error
    );
  }
}

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  register: (data: { email: string; password: string; name: string; website?: string }) =>
    fetchApi<ApiResponse<{ user?: unknown; verificationRequired?: boolean; email?: string; message?: string }>>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { email: string; password: string; mfaCode?: string }) =>
    fetchApi<ApiResponse<{ user: unknown; message: string }>>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  logout: () =>
    fetchApi<ApiResponse<{ message: string }>>('/auth/logout', {
      method: 'POST',
    }),

  me: () =>
    fetchApi<ApiResponse<{ user: unknown }>>('/auth/me'),

  forgotPassword: (email: string) =>
    fetchApi<ApiResponse<{ message: string }>>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    fetchApi<ApiResponse<{ message: string }>>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  verifyEmail: (data: { email: string; code: string }) =>
    fetchApi<ApiResponse<{ user: unknown; message?: string }>>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resendVerification: (email: string) =>
    fetchApi<ApiResponse<{ message: string }>>('/auth/verification/resend', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  updateProfile: (data: { name?: string; avatarUrl?: string }) =>
    fetchApi<ApiResponse<{ user: unknown; message: string }>>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    fetchApi<ApiResponse<{ message: string }>>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // MFA (TOTP)
  mfaEnroll: () =>
    fetchApi<ApiResponse<{ secret: string; otpauthUri: string; backupCodes: string[]; message: string }>>('/auth/mfa/enroll', {
      method: 'POST',
    }),

  mfaVerify: (code: string) =>
    fetchApi<ApiResponse<{ enabled: boolean }>>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  mfaDisable: (code: string) =>
    fetchApi<ApiResponse<{ disabled: boolean }>>('/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

// ============================================================================
// Pools API
// ============================================================================

export const poolsApi = {
  list: () =>
    fetchApi<ApiResponse<Pool[]>>('/pools'),

  get: (slug: string) =>
    fetchApi<ApiResponse<{ pool: Pool; userRole: string | null }>>(`/pools/${slug}`),

  create: (data: { name: string; description?: string; isPublic: boolean }) =>
    fetchApi<ApiResponse<{ pool: Pool; message: string }>>('/pools', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (slug: string, data: { name?: string; description?: string; isPublic?: boolean }) =>
    fetchApi<ApiResponse<{ pool: Pool; message: string }>>(`/pools/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  join: (slug: string, code: string) =>
    fetchApi<ApiResponse<{ message: string }>>(`/pools/${slug}/join`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  leave: (slug: string) =>
    fetchApi<ApiResponse<{ message: string }>>(`/pools/${slug}/leave`, {
      method: 'POST',
    }),

  getRanking: (slug: string) =>
    fetchApi<ApiResponse<Ranking[]>>(`/pools/${slug}/ranking`),

  getMembers: (slug: string) =>
    fetchApi<ApiResponse<unknown[]>>(`/pools/${slug}/members`),
};

// ============================================================================
// Matches API
// ============================================================================

export const matchesApi = {
  list: (params?: { stage?: string; status?: string; date?: string; page?: number; limit?: number; isDemo?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<Match>>(
      `/matches${query ? `?${query}` : ''}`
    );
  },

  upcoming: (params?: { isDemo?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.isDemo !== undefined) {
      searchParams.set('isDemo', String(params.isDemo));
    }
    const query = searchParams.toString();
    return fetchApi<ApiResponse<Match[]>>(`/matches/upcoming${query ? `?${query}` : ''}`);
  },

  live: (params?: { isDemo?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.isDemo !== undefined) {
      searchParams.set('isDemo', String(params.isDemo));
    }
    const query = searchParams.toString();
    return fetchApi<ApiResponse<Match[]>>(`/matches/live${query ? `?${query}` : ''}`);
  },

  get: (id: number, params?: { isDemo?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.isDemo !== undefined) {
      searchParams.set('isDemo', String(params.isDemo));
    }
    const query = searchParams.toString();
    return fetchApi<ApiResponse<Match>>(`/matches/${id}${query ? `?${query}` : ''}`);
  },

  updateScore: (id: number, homeScore: number, awayScore: number, status?: string) =>
    fetchApi<ApiResponse<{ match: Match; message: string }>>(`/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ homeScore, awayScore, status }),
    }),

  recalculate: () =>
    fetchApi<ApiResponse<{ message: string }>>('/matches/recalculate', {
      method: 'POST',
    }),
};

// ============================================================================
// Predictions API
// ============================================================================

export const predictionsApi = {
  getMyPredictions: (poolSlug: string) =>
    fetchApi<ApiResponse<unknown[]>>(`/pools/${poolSlug}/predictions`),

  getMatchPredictions: (poolSlug: string, matchId: number) =>
    fetchApi<ApiResponse<unknown[]>>(`/pools/${poolSlug}/predictions/${matchId}`),

  createOrUpdate: (poolSlug: string, matchId: number, homeScore: number, awayScore: number) =>
    fetchApi<ApiResponse<{ message: string }>>(`/pools/${poolSlug}/predictions`, {
      method: 'POST',
      body: JSON.stringify({ matchId, homeScore, awayScore }),
    }),

  getMyStats: (poolSlug: string) =>
    fetchApi<ApiResponse<unknown>>(`/pools/${poolSlug}/my-stats`),
};

// ============================================================================
// Special Bets API
// ============================================================================

export const specialsApi = {
  list: () =>
    fetchApi<ApiResponse<unknown[]>>('/special-bets'),

  getMyPredictions: (poolSlug: string) =>
    fetchApi<ApiResponse<unknown[]>>(`/special-bets/pools/${poolSlug}/special-predictions`),

  createOrUpdate: (poolSlug: string, betId: number, prediction: string) =>
    fetchApi<ApiResponse<{ message: string }>>(`/special-bets/pools/${poolSlug}/special-predictions`, {
      method: 'POST',
      body: JSON.stringify({ betId, prediction }),
    }),

  setCorrectAnswer: (betId: number, correctAnswer: string) =>
    fetchApi<ApiResponse<{ message: string }>>(`/special-bets/${betId}`, {
      method: 'PUT',
      body: JSON.stringify({ correctAnswer }),
    }),
};

// ============================================================================
// Admin API
// ============================================================================

export const adminApi = {
  // Dashboard
  getDashboard: () =>
    fetchApi<ApiResponse<DashboardMetrics>>('/admin/dashboard'),

  // Users
  getUsers: (params?: { search?: string; isAdmin?: boolean; page?: number; limit?: number; sortBy?: string; sortOrder?: string }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ data: unknown[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/users${query ? `?${query}` : ''}`
    );
  },

  getUser: (id: number) =>
    fetchApi<ApiResponse<unknown>>(`/admin/users/${id}`),

  updateUser: (id: number, data: { name?: string; email?: string; isAdmin?: boolean; password?: string }) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  elevateAdmin: (id: number, data: { justification: string; mfaCode: string }) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/users/${id}/elevate-admin`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  revokeAdmin: (id: number, data: { justification: string; mfaCode: string }) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/users/${id}/revoke-admin`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Privilege approval workflow (AUTHZ-001)
  listPrivilegeRequests: (status: 'pending' | 'all' = 'pending') =>
    fetchApi<ApiResponse<unknown[]>>(`/admin/privilege-requests?status=${encodeURIComponent(status)}`),

  approvePrivilegeRequest: (requestId: string, data: { justification: string; mfaCode: string }) =>
    fetchApi<ApiResponse<{ message: string; requestId: string }>>(`/admin/privilege-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  rejectPrivilegeRequest: (requestId: string, data: { justification: string; mfaCode: string }) =>
    fetchApi<ApiResponse<{ message: string; requestId: string }>>(`/admin/privilege-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteUser: (id: number) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/users/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm: true }),
    }),

  bulkDeleteUsers: (ids: number[]) =>
    fetchApi<ApiResponse<{ message: string; deletedCount: number }>>('/admin/users/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids, confirm: true }),
    }),

  bulkUpdateUsers: (ids: number[], data: { isAdmin?: boolean }) =>
    fetchApi<ApiResponse<{ message: string; updatedCount: number }>>('/admin/users/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ ids, data }),
    }),

  // Pools
  getPools: (params?: { search?: string; isPublic?: boolean; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ data: unknown[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/pools${query ? `?${query}` : ''}`
    );
  },

  deletePool: (slug: string) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/pools/${slug}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm: true }),
    }),

  // Predictions
  getPredictions: (params?: { userId?: number; poolId?: number; matchId?: number; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ data: unknown[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/predictions${query ? `?${query}` : ''}`
    );
  },

  deletePrediction: (id: number) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/predictions/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm: true }),
    }),

  // Audit
  getAuditLogs: (params?: { adminId?: number; action?: string; entityType?: string; startDate?: string; endDate?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ data: AuditLogView[]; total: number; page: number; limit: number; totalPages: number }>(
      `/admin/audit${query ? `?${query}` : ''}`
    );
  },

  getAuditLog: (id: number) => fetchApi<{ data: AuditLogView }>(`/admin/audit/${id}`),

  exportAuditLogs: (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    const query = searchParams.toString();
    return `${getApiBase()}/admin/audit/export${query ? `?${query}` : ''}`;
  },

  // System
  getSystem: () =>
    fetchApi<ApiResponse<SystemHealth>>('/admin/system'),

  // Demo mode
  getDemoStatus: () =>
    fetchApi<ApiResponse<{ pools: number; lastPoolId: number | null; demoMatches: number; freeUsers: number; flags: Record<string, boolean> }>>('/admin/demo/status'),

  seedDemo: () =>
    fetchApi<ApiResponse<{ message: string; poolSlug: string }>>('/admin/demo/seed', { method: 'POST' }),

  resetDemo: (poolId: number) =>
    fetchApi<ApiResponse<{ message: string }>>('/admin/demo/reset', { method: 'POST', body: JSON.stringify({ poolId }) }),

  finishAllDemo: (poolId: number) =>
    fetchApi<ApiResponse<{ message: string }>>('/admin/demo/finish-all', { method: 'POST', body: JSON.stringify({ poolId }) }),

  finishDemoMatch: (matchId: number) =>
    fetchApi<ApiResponse<{ message: string }>>(`/admin/demo/finish/${matchId}`, { method: 'POST' }),
};
